export type BackendTheme = 'LIGHT' | 'DARK';
export type BackendChatIconAlign = 'LEFT' | 'RIGHT';

export type ColorHex = `#${string}`;
export const isColorHexWithoutAlpha = (color: string): color is ColorHex => {
    return color.startsWith('#') && color.length === 7;
};
export const isColorHexOrTransparent = (color: string): color is ColorHex | ColorTransparent => {
    return isColorHexWithoutAlpha(color) || color === 'transparent';
};
export type ColorTransparent = 'transparent';

export interface BackendStyleOptionsResponse {
    theme?: BackendTheme;
    primary_bg_color?: string;
    chat_icon_bg_color?: string;
    chat_icon_align?: BackendChatIconAlign;
}
export const THEMES = {
    light: 'light',
    dark: 'dark',
} as const;
export type Theme = (typeof THEMES)[keyof typeof THEMES];

export const CHAT_ICON_ALIGN = {
    left: 'left',
    right: 'right',
} as const;
export type ChatIconAlign = (typeof CHAT_ICON_ALIGN)[keyof typeof CHAT_ICON_ALIGN];

export interface StyleOptionsConfig {
    theme?: Theme;
    primaryBgColor?: string;
    chatIconBgColor?: string;
    chatIconAlign?: ChatIconAlign;
    transparentBg?: boolean;
}

export class StyleOptions {
    theme?: Theme;
    primaryBgColor?: string;
    chatIconBgColor?: string;
    chatIconAlign?: ChatIconAlign;
    transparentBg = false;

    constructor(config: StyleOptionsConfig) {
        this.theme = config.theme;
        this.primaryBgColor = config.primaryBgColor;
        this.chatIconBgColor = config.chatIconBgColor;
        this.chatIconAlign = config.chatIconAlign;
        this.transparentBg = config.transparentBg ?? false;
    }

    public static fromBackendResponse(backendData: BackendStyleOptionsResponse): StyleOptions {
        const iconColor = this.formatColorFromBackend(backendData.chat_icon_bg_color);

        const stylePrefs = new StyleOptions({
            theme: backendData.theme?.toLowerCase() as Theme | undefined,
            primaryBgColor: backendData.primary_bg_color ?? undefined,
            chatIconBgColor: iconColor?.opaque,
            chatIconAlign: backendData.chat_icon_align?.toLowerCase() as ChatIconAlign | undefined,
            transparentBg: iconColor?.transparent,
        });
        return stylePrefs;
    }

    private static formatColorFromBackend(color: string | undefined) {
        if (!color) return;
        const opaque = color.slice(0, 7);
        const alpha = color.slice(7);
        return { opaque, transparent: alpha === '00' };
    }

    public static toBackendRequest(styleOptions: StyleOptions): BackendStyleOptionsResponse {
        const prefs = {
            theme: styleOptions.theme?.toUpperCase() as BackendTheme | undefined,
            primary_bg_color: styleOptions.primaryBgColor,
            chat_icon_bg_color: styleOptions.chatIconBgColor,
            chat_icon_align: styleOptions.chatIconAlign?.toUpperCase() as
                | BackendChatIconAlign
                | undefined,
        };
        if (prefs.chat_icon_bg_color && styleOptions.transparentBg) {
            // add alpha channel
            prefs.chat_icon_bg_color = prefs.chat_icon_bg_color?.slice(0, 7) + '00';
        } else {
            prefs.chat_icon_bg_color = prefs.chat_icon_bg_color?.slice(0, 7);
        }
        return prefs;
    }
}
